import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Stack, Box, Typography, Pill} from 'views/components';
import {translate, SimpleRefreshWidget} from 'views/module';

import {StageDropdown, CallStatusDropdown, BookCallDropdown, LeadQualityDropdown} from './dropdowns';

import moment from 'moment';

const ClientDetails = (props) => {
  const {
    clientBackend,
    clientData,
    clientCallStatus,
    clientCareplan,
    user,
    refresh,
    setRefresh,
  } = props;

  const [stage, setStage] = React.useState(clientData.status);
  const [callStatus, setCallStatus] = React.useState(clientCallStatus ?? '');
  const [leadQuality, setLeadQuality] = React.useState(clientCareplan?.leadQuality);
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState(new Date());

  const nullChar = '-';

  // Fetch client data
  const calendlyUserSlug = React.useMemo(() => {
    const userFirstName = user?.firstName?.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z0-9-]/g, '');
    return userFirstName ? `${userFirstName}-carequilt` : null;
  }, [user?.firstName]);

  const clientRelationships = React.useMemo(() => {
    const relationships = Object.values(clientData.relationships ?? {}).map((relationship) => {
      const relationshipName = (relationship.name ?? '').trim();
      const email = relationship.contacts?.find((x) => x?.type?.toLowerCase().includes('mail'))?.detail ?? '';
      const phoneNumber = relationship.contacts?.find((x) =>
        x?.type?.toLowerCase().includes('phone') ||
        x?.type?.toLowerCase().includes('number'),
      )?.detail ?? '';
      const [firstName = '', ...lastNameParts] = relationshipName.split(/\s+/).filter(Boolean);
      const params = new URLSearchParams({
        utm_term: relationship.relationshipRn ?? '',
        location: phoneNumber,
        name: relationshipName,
        email,
        utm_source: email,
        first_name: firstName,
        last_name: lastNameParts.join(' '),
      });

      return {
        ...relationship,
        calendlyLink: calendlyUserSlug ?
          `https://calendly.com/${calendlyUserSlug}?${params.toString()}` :
          null,
        email,
        phoneNumber,
      };
    });

    return relationships.sort((a, b) => {
      if ((a.priority ?? Number.MAX_SAFE_INTEGER) !== (b.priority ?? Number.MAX_SAFE_INTEGER)) {
        return (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER);
      }

      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }, [clientData.relationships, calendlyUserSlug]);
  const primaryRelationship = clientRelationships[0] ?? null;

  const clientCreatedAt = clientData.createdAt ?? null;
  const daysFromCreation = React.useMemo(() => {
    if (!clientCreatedAt) return 0;
    return moment().diff(moment(clientCreatedAt), 'days');
  }, [clientCreatedAt]);
  const clientLocation = clientCareplan?.generalInformation?.address ? clientCareplan?.generalInformation?.address : nullChar;

  // Handlers

  const handleSetStage = (selectedStage, message=null) => {
    if (!selectedStage || selectedStage === stage) return;
    setStage(selectedStage);

    clientBackend.detail.editStatus(selectedStage, () => {}, () => {}, message);
  };

  const handleSetCallStatus = (selectedCallStatus) => {
    if (!selectedCallStatus) return;
    setCallStatus(selectedCallStatus);

    clientBackend.detail.editCallStatus(selectedCallStatus, () => {});
  };

  const handleSetLeadQuality = async (selectedLeadQuality) => {
    const nextLeadQuality = selectedLeadQuality || 'NONE';
    setLeadQuality(nextLeadQuality);

    await clientBackend.careplan.updateLeadQuality(nextLeadQuality === 'NONE' ? null : nextLeadQuality);
  };

  const metricsSections = React.useMemo(() => {
    return [
      {
        key: 'primaryContact',
        label: translate('client.ClientDetail.Status.Metrics.PrimaryContact'),
        value: primaryRelationship?.name || nullChar,
        cellSx: {
          justifyContent: {
            xs: 'center',
            xl: 'flex-start',
          },
        },
      },
      {
        key: 'contactEmail',
        label: translate('client.ClientDetail.Status.Metrics.ContactEmail'),
        value: primaryRelationship?.email || nullChar,
      },
      {
        key: 'contactPhone',
        label: translate('client.ClientDetail.Status.Metrics.ContactPhone'),
        value: primaryRelationship?.phoneNumber || nullChar,
      },
      {
        key: 'calendly',
        label: translate('client.ClientDetail.Status.Metrics.Calendly'),
        type: 'bookCallDropdown',
      },
      {
        key: 'stage',
        label: translate('client.ClientDetail.Status.Menu.Subtitles.Stage'),
        type: 'stageDropdown',
      },
      {
        key: 'leadQuality',
        label: translate('client.ClientDetail.Status.Menu.Subtitles.LeadQuality'),
        type: 'leadQualityDropdown',
      },
      {
        key: 'introCall',
        label: translate('client.ClientDetail.Status.Metrics.IntroCall'),
        type: 'callStatusDropdown',
        cellSx: {
          justifyContent: {
            xs: 'center',
            xl: 'flex-end',
          },
        },
      },
    ];
  }, [nullChar, primaryRelationship]);

  return (
    <Stack sx={{gap: '10px'}}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems={{xs: 'flex-start', md: 'center'}}
        sx={{gap: '10px', flexWrap: 'wrap'}}
      >
        <Stack
          direction='row'
          sx={{
            gap: '7px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Pill
            label={translate('client.ClientDetail.Status.Subtitles.Day', {number: daysFromCreation})}
            variant='info'
          />
          {
            clientLocation !== nullChar &&
            <Pill
              label={clientLocation}
              variant='neutral'
            />
          }
          {
            clientCreatedAt &&
            <Pill
              label={translate('client.ClientDetail.Status.Subtitles.Created', {
                value: moment(clientCreatedAt).format(translate('timeFormat.ShortMonthDayYear')),
              })}
              variant='neutral'
            />
          }
          <Pill
            label={translate('client.ClientDetail.Status.Subtitles.Updated', {
              value: moment(lastUpdatedAt).format(translate('timeFormat.ShortMonthDayTime')),
            })}
            variant='neutral'
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          sx={{gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto'}}
        >
          <SimpleRefreshWidget
            numRefresh={refresh}
            setNumRefresh={setRefresh}
            setLastUpdatedAt={setLastUpdatedAt}
          />
        </Stack>
      </Stack>
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: '1px',
            backgroundColor: '#E8E4DE',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, auto))',
              sm: 'repeat(2, minmax(0, auto))',
              md: 'repeat(3, minmax(0, auto))',
              xl: 'repeat(7, minmax(0, auto))',
            },
          }}
        >
          {
            metricsSections.map((section) => {
              return (
                <Box
                  key={section.key}
                  sx={{
                    minWidth: 0,
                    backgroundColor: '#FFFFFF',
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...section.cellSx,
                  }}
                >
                  {
                    section.type === 'stageDropdown' &&
                    <StageDropdown
                      label={section.label}
                      selectedStage={stage}
                      setSelectedStage={handleSetStage}
                      variant='compact'
                    />
                  }
                  {
                    section.type === 'leadQualityDropdown' &&
                    <LeadQualityDropdown
                      label={section.label}
                      selectedLeadQuality={leadQuality}
                      setSelectedLeadQuality={handleSetLeadQuality}
                      variant='compact'
                    />
                  }
                  {
                    section.type === 'callStatusDropdown' &&
                    <CallStatusDropdown
                      label={section.label}
                      selectedStatus={callStatus}
                      setSelectedStatus={handleSetCallStatus}
                      variant='compact'
                    />
                  }
                  {
                    section.type === 'bookCallDropdown' &&
                    <BookCallDropdown
                      currentUser={user}
                      label={section.label}
                      primaryRelationship={primaryRelationship}
                    />
                  }
                  {
                    !section.type &&
                    <Stack
                      sx={{
                        gap: '6px',
                        minWidth: 0,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '11px',
                          lineHeight: '14px',
                          color: '#A8A49E',
                        }}
                      >
                        {section.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: '#000000',
                          wordBreak: 'break-word',
                        }}
                      >
                        {section.value}
                      </Typography>
                    </Stack>
                  }
                </Box>
              );
            })
          }
        </Box>
      </Box>
    </Stack>
  );
};
ClientDetails.propTypes = {
  clientRn: PropTypes.string.isRequired,
  clientData: PropTypes.object.isRequired,
  clientBackend: PropTypes.object.isRequired,
  clientCareplan: PropTypes.object,
  clientCallStatus: PropTypes.string,
  refresh: PropTypes.number.isRequired,
  setRefresh: PropTypes.func.isRequired,
  user: PropTypes.object,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientDetails));
